/**
 * @fileoverview Criar e editar um departamento: o nome, as contas da empresa
 * (cada uma em um departamento só: as que já estão em outro aparecem
 * desligadas, com o nome dele) e quem vê os números.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Chip, Dialog, IconButton, TextField, Typography, makeStyles } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Pencil, Plus, X } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { createModalDesign } from "../../styles/modalDesign";

const useStyles = makeStyles((theme) => ({
  ...createModalDesign(theme),
  fields: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  option: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    width: "100%",
    minWidth: 0,
  },
  optionName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionHint: {
    flexShrink: 0,
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "#94a3b8" : "#64748b",
  },
}));

const byId = (list, ids) => list.filter((item) => ids.includes(item.id));

const DepartmentModal = ({ open, onClose, onSubmit, department, options, departmentNames, loading }) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [companyIds, setCompanyIds] = useState([]);
  const [viewerIds, setViewerIds] = useState([]);
  const isEdit = !!department?.id;
  const companies = useMemo(() => options?.companies || [], [options]);
  const people = useMemo(() => options?.people || [], [options]);

  useEffect(() => {
    if (!open) return;
    setName(department?.name || "");
    setCompanyIds((department?.companies || []).map((company) => company.id));
    setViewerIds((department?.viewers || []).map((viewer) => viewer.id));
  }, [open, department]);

  // Conta que já está em outro departamento: desligada, com o nome dele.
  const takenBy = (company) =>
    company.departmentId && company.departmentId !== department?.id ? departmentNames.get(company.departmentId) || "" : null;

  const canSave = !loading && name.trim() && companyIds.length > 0;

  const chips = (value, getTagProps) =>
    value.map((option, index) => <Chip variant="outlined" size="small" label={option.name} {...getTagProps({ index })} />);

  return (
    <Dialog open={open} onClose={onClose} className={`${classes.mdDialog} ${classes.mdDialogWide}`} maxWidth={false}>
      <div className={classes.mdHeader}>
        <Typography className={classes.mdTitle}>
          {i18n.t(isEdit ? "settings.departments.modal.edit" : "settings.departments.modal.add")}
        </Typography>
        <IconButton size="small" onClick={onClose} className={classes.mdCloseBtn}>
          <X size={18} strokeWidth={1.75} />
        </IconButton>
      </div>

      <div className={classes.mdContent}>
        <div className={classes.fields}>
          <div>
            <span className={classes.mdFieldLabel}>{i18n.t("settings.departments.form.name")}</span>
            <TextField
              variant="outlined"
              value={name}
              onChange={(event) => setName(event.target.value)}
              inputProps={{ maxLength: 80, "aria-label": i18n.t("settings.departments.form.name") }}
              fullWidth
              autoFocus
              className={classes.mdField}
            />
          </div>

          <div>
          <span className={classes.mdFieldLabel}>{i18n.t("settings.departments.form.companies")}</span>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={companies}
            value={byId(companies, companyIds)}
            onChange={(_event, value) => setCompanyIds(value.map((company) => company.id))}
            getOptionLabel={(company) => company.name}
            getOptionSelected={(option, value) => option.id === value.id}
            getOptionDisabled={(company) => takenBy(company) !== null}
            noOptionsText={i18n.t("settings.departments.form.noOptions")}
            renderTags={chips}
            renderOption={(company) => {
              const owner = takenBy(company);
              return (
                <span className={classes.option}>
                  <span className={classes.optionName}>{company.name}</span>
                  {owner !== null && (
                    <span className={classes.optionHint}>{i18n.t("settings.departments.form.takenBy", { name: owner })}</span>
                  )}
                </span>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                inputProps={{ ...params.inputProps, "aria-label": i18n.t("settings.departments.form.companies") }}
                helperText={i18n.t("settings.departments.form.companiesHelp")}
                className={classes.mdField}
              />
            )}
          />
          </div>

          <div>
          <span className={classes.mdFieldLabel}>{i18n.t("settings.departments.form.viewers")}</span>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={people}
            value={byId(people, viewerIds)}
            onChange={(_event, value) => setViewerIds(value.map((person) => person.id))}
            getOptionLabel={(person) => person.name}
            getOptionSelected={(option, value) => option.id === value.id}
            noOptionsText={i18n.t("settings.departments.form.noOptions")}
            renderTags={chips}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                inputProps={{ ...params.inputProps, "aria-label": i18n.t("settings.departments.form.viewers") }}
                helperText={i18n.t("settings.departments.form.viewersHelp")}
                className={classes.mdField}
              />
            )}
          />
          </div>
        </div>
      </div>

      <div className={classes.mdActions}>
        <button type="button" onClick={onClose} disabled={loading} className={classes.mdBtnGhost}>
          {i18n.t("settings.departments.buttons.cancel")}
        </button>
        <button
          type="button"
          onClick={() => onSubmit({ name: name.trim(), companyIds, viewerIds })}
          disabled={!canSave}
          className={classes.mdBtnPrimary}
        >
          {isEdit ? <Pencil size={15} strokeWidth={1.75} /> : <Plus size={15} strokeWidth={1.75} />}
          {i18n.t("settings.departments.buttons.save")}
        </button>
      </div>
    </Dialog>
  );
};

export default DepartmentModal;
